/**
 * Particle class - represents a single sand particle
 * Ported from original working implementation
 */
export class Particle {
    constructor(x, y, radius, color) {
        this.x = x;
        this.y = y;
        this.vx = (Math.random() - 0.5) * 2;
        this.vy = (Math.random() - 0.5) * 2;
        this.radius = radius;
        this.baseColor = color;
        this.color = color;
        this.mass = radius;
        this.claimIntensity = 0;
    }

    update(circleRadius, centerX, centerY) {
        // Brownian motion
        this.vx += (Math.random() - 0.5) * 0.15;
        this.vy += (Math.random() - 0.5) * 0.15;
        
        // Drift toward center
        let dx = centerX - this.x;
        let dy = centerY - this.y;
        let distanceFromCenter = Math.sqrt(dx * dx + dy * dy);
        const centerPull = 0.005;
        if (distanceFromCenter > 0) {
            this.vx += (dx / distanceFromCenter) * centerPull;
            this.vy += (dy / distanceFromCenter) * centerPull;
        }
        
        // Cap velocity
        const maxSpeed = 1.5;
        const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (speed > maxSpeed) {
            this.vx = (this.vx / speed) * maxSpeed;
            this.vy = (this.vy / speed) * maxSpeed;
        }
        
        // Update position
        this.x += this.vx;
        this.y += this.vy;
        
        // Friction
        this.vx *= 0.98;
        this.vy *= 0.98;
        
        // Boundary collision
        dx = this.x - centerX;
        dy = this.y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance + this.radius > circleRadius) {
            const angle = Math.atan2(dy, dx);
            this.x = centerX + Math.cos(angle) * (circleRadius - this.radius);
            this.y = centerY + Math.sin(angle) * (circleRadius - this.radius);
            
            const normalX = dx / distance;
            const normalY = dy / distance;
            const dotProduct = this.vx * normalX + this.vy * normalY;
            
            this.vx = (this.vx - 2 * dotProduct * normalX) * 0.9;
            this.vy = (this.vy - 2 * dotProduct * normalY) * 0.9;
        }
    }

    applyClaim(intensity = 0.5) {
        this.claimIntensity = Math.min(1, this.claimIntensity + intensity);
    }

    updateClaimColor() {
        if (this.claimIntensity > 0) {
            this.claimIntensity *= 0.97;
            if (this.claimIntensity < 0.01) this.claimIntensity = 0;
        }
        
        if (this.claimIntensity > 0) {
            this.color = this.interpolateColor(this.baseColor, '#ff3333', this.claimIntensity);
        } else {
            this.color = this.baseColor;
        }
    }

    interpolateColor(color1, color2, factor) {
        const c1 = this.hexToRgb(color1);
        const c2 = this.hexToRgb(color2);
        
        const r = Math.round(c1.r + (c2.r - c1.r) * factor);
        const g = Math.round(c1.g + (c2.g - c1.g) * factor);
        const b = Math.round(c1.b + (c2.b - c1.b) * factor);
        
        return `rgb(${r}, ${g}, ${b})`;
    }

    hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 255, g: 255, b: 255 };
    }

    draw(ctx) {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
    }

    collidesWith(other) {
        const dx = this.x - other.x;
        const dy = this.y - other.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance < this.radius + other.radius;
    }

    resolveCollision(other) {
        const dx = other.x - this.x;
        const dy = other.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance === 0) return;
        
        const nx = dx / distance;
        const ny = dy / distance;
        
        const dvx = other.vx - this.vx;
        const dvy = other.vy - this.vy;
        const dvn = dvx * nx + dvy * ny;
        
        if (dvn > 0) return;
        
        const restitution = 0.8;
        const impulse = ((1 + restitution) * dvn) / (this.mass + other.mass);
        
        this.vx += impulse * other.mass * nx;
        this.vy += impulse * other.mass * ny;
        other.vx -= impulse * this.mass * nx;
        other.vy -= impulse * this.mass * ny;
        
        const overlap = this.radius + other.radius - distance;
        const separationX = (overlap / 2) * nx;
        const separationY = (overlap / 2) * ny;
        
        this.x -= separationX;
        this.y -= separationY;
        other.x += separationX;
        other.y += separationY;
    }
}

